import { PALETTE } from "./workflowPaletteConstants";

export const COLLECT_INPUT_ALLOWED_EXPECTED_TYPES = new Set(["text", "image", "document"]);

export const COLLECT_INPUT_EXPECTED_TYPE_OPTIONS = [
  { value: "text", label: "Text message" },
  { value: "image", label: "Image upload" },
  { value: "document", label: "Document / file upload" }
];

export function filterCollectInputExpectedTypeOptions(options = []) {
  const filtered = options.filter((opt) => COLLECT_INPUT_ALLOWED_EXPECTED_TYPES.has(opt.value));
  return filtered.length ? filtered : COLLECT_INPUT_EXPECTED_TYPE_OPTIONS;
}

/** Fallback when GET_CATALOG omits generic.collect_input configSchema. */
export const COLLECT_INPUT_CONFIG_SCHEMA = {
  expectedType: {
    type: "select",
    default: "text",
    options: COLLECT_INPUT_EXPECTED_TYPE_OPTIONS,
    hint: "What the customer should send in reply."
  },
  storeAs: {
    type: "text",
    default: "",
    hint: "Internal name for the saved answer (e.g. name, selfie, document)."
  },
  prompt: {
    type: "textarea",
    default: "",
    placeholder: "Enter your name",
    hint: "Message asking the customer for input."
  },
  invalidTypeMessage: {
    type: "textarea",
    default: "",
    placeholder: "Please send text only",
    hint: "Reply when the customer sends the wrong type."
  }
};

/** Fallback when GET_CATALOG omits workflow picker fields on invoke / transfer nodes. */
export const WORKFLOW_PICKER_CONFIG_SCHEMA = {
  workflowId: {
    type: "select",
    default: "",
    hint: "Choose a workflow from your account."
  }
};

/** API configSchema / inputSchema: object keyed by field name. */
export function schemaToFields(schema) {
  if (!schema || typeof schema !== "object") return [];

  return Object.entries(schema).map(([name, entry]) => {
    if (entry.type) {
      return { name, ...entry };
    }
    return { name, type: "object", fields: entry };
  });
}

export function getDefaultValue(field) {
  if (field.default !== undefined) return field.default;
  if (field.type === "expression") return "";
  if (field.type === "keyvalue") return {};
  if (field.type === "array") return [];
  if (field.type === "object") return {};
  if (field.type === "boolean" || field.type === "checkbox") return false;
  return "";
}

const NUMERIC_CONDITION_OPERATORS = new Set(["greater_than", "less_than"]);

/** Parse condition right-hand side: numbers only for numeric ops; preserve $expressions. */
export function parseConditionRightValue(raw, operator = "") {
  if (raw === "" || raw == null) return "";
  if (typeof raw === "boolean") return raw;

  const trimmed = String(raw).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("$")) return trimmed;

  if (NUMERIC_CONDITION_OPERATORS.has(operator)) {
    const num = Number(trimmed);
    return Number.isNaN(num) ? trimmed : num;
  }

  if (trimmed === "true") return true;
  if (trimmed === "false") return false;

  return trimmed;
}

/** API condition.right is a string for text ops (e.g. equals after collect_input); numbers for numeric ops. */
export function formatConditionRightForSave(right, operator = "") {
  if (right === "" || right == null) return right;
  if (typeof right === "boolean") return right;

  const trimmed = String(right).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("$")) return trimmed;

  if (NUMERIC_CONDITION_OPERATORS.has(operator)) {
    if (typeof right === "number") return right;
    const num = Number(trimmed);
    return Number.isNaN(num) ? trimmed : num;
  }

  return trimmed;
}

export function formatConditionRightForInput(value) {
  if (value === "" || value == null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const CONDITION_CONTEXT_PREFIX = "$context.";

/** API condition.left uses $context.key; inspector shows the bare key. */
export function formatConditionLeftForSave(left) {
  if (left === "" || left == null) return left;
  const trimmed = String(left).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("$")) return trimmed;
  return `${CONDITION_CONTEXT_PREFIX}${trimmed}`;
}

export function formatConditionLeftForInput(left) {
  if (left === "" || left == null) return left;
  const trimmed = String(left).trim();
  if (trimmed.startsWith(CONDITION_CONTEXT_PREFIX)) {
    return trimmed.slice(CONDITION_CONTEXT_PREFIX.length);
  }
  return trimmed;
}

export function serializeConditionsForSave(conditions = []) {
  if (!Array.isArray(conditions)) return [];
  return conditions.map((row) => ({
    ...row,
    left: formatConditionLeftForSave(row.left),
    right: formatConditionRightForSave(row.right, row.operator),
  }));
}

export function deserializeConditionsFromApi(conditions = []) {
  if (!Array.isArray(conditions)) return [];
  return conditions.map((row) => ({
    ...row,
    left: formatConditionLeftForInput(row.left),
  }));
}

/** All draggable palette entries from GET_CATALOG.palette plus custom actions. */
export function getCustomActionPaletteEntries(catalogData) {
  const actions = catalogData?.actions ?? [];
  return actions
    .filter((action) => action.source === "custom" || String(action.type || "").startsWith("CUSTOM:"))
    .map((action) => {
      const actionId =
        action._meta?.actionId ||
        String(action.type || "").replace(/^CUSTOM:/, "");
      return {
        paletteType: `custom.${actionId}`,
        channel: "custom",
        label: action.label || actionId,
        description: action.description || "",
        icon: "custom-action",
        nodeType: "ACTION",
        defaultConfig: {
          action: action.type,
          input: {}
        },
        configSchema: {},
        transitionHints: ["onSuccess", "onFailure"],
        category: (action.category || "custom").toLowerCase()
      };
    });
}

export function getPaletteCatalogEntries(catalogData) {
  const palette = catalogData?.palette ?? {};
  return [...Object.values(palette).flat(), ...getCustomActionPaletteEntries(catalogData)];
}

export function isSchemaFieldVisible(field, context = {}) {
  if (!field?.visibleWhen) return true;
  try {
    const evaluator = new Function("method", `return (${field.visibleWhen});`);
    return Boolean(evaluator(context.method));
  } catch {
    return false;
  }
}

export function buildUnifiedMessageSchema(catalogData, suffix) {
  const entries = getPaletteCatalogEntries(catalogData);
  const variants = {};

  const typeToPaletteSegment = {
    text: "text",
    image: "image",
    video: "video",
    document: "document",
    buttons: "buttons",
    list: "list",
    template: "template_text"
  };

  const messageTypes = suffix === ".notify"
    ? ["text", "image", "video", "document", "template"]
    : ["text", "buttons", "list", "image", "video", "document", "template"];

  messageTypes.forEach((mType) => {
    const segment = typeToPaletteSegment[mType];
    const paletteType = `whatsapp.${segment}${suffix}`;
    const entry = entries.find(e => e.paletteType === paletteType);
    
    let fields = {};
    if (entry?.configSchema?.message?.fields) {
      fields = { ...entry.configSchema.message.fields };
    } else {
      if (mType === "text") {
        fields = { text: { type: "textarea", supportsTemplates: true } };
      } else if (mType === "image" || mType === "video") {
        fields = {
          url: { type: "text", supportsTemplates: true },
          caption: { type: "textarea", supportsTemplates: true }
        };
      } else if (mType === "document") {
        fields = {
          url: { type: "text", supportsTemplates: true },
          filename: { type: "text" },
          caption: { type: "textarea", supportsTemplates: true }
        };
      } else if (mType === "buttons") {
        fields = {
          text: { type: "textarea", supportsTemplates: true },
          options: {
            type: "array",
            itemSchema: {
              label: { type: "text" }
            }
          }
        };
      } else if (mType === "list") {
        fields = {
          text: { type: "textarea", supportsTemplates: true },
          buttonLabel: { type: "text" },
          sectionTitle: { type: "text" },
          items: {
            type: "array",
            maxItems: 15,
            itemSchema: {
              label: { type: "text" },
              description: { type: "text" }
            }
          }
        };
      }
    }

    if (fields.options && fields.options.type === "array") {
      fields.options = {
        ...fields.options,
        maxItems: 3
      };
    }

    if (fields.items && fields.items.type === "array") {
      fields.items = {
        ...fields.items,
        maxItems: 15
      };
    }

    variants[mType] = fields;
  });

  const genericNodeType = suffix === ".notify" ? "NOTIFY_USER" : "AWAIT_USER_REPLY";
  const genericNode = (catalogData?.nodes ?? []).find((node) => node.type === genericNodeType);
  const genericSchema = genericNode?.configSchema ?? {};
  const recipientIdField = {
    ...(genericSchema.recipientId || {}),
    type: "expression",
    default: "",
    hint: "Optional. Defaults to the conversation user. Use for cross-channel sends (e.g. $context.userEmail)."
  };

  return {
    message: {
      type: "dynamic",
      dependsOn: "type",
      variants
    },
    recipientId: recipientIdField
  };
}

/**
 * Palette entries carry editor slices; node definitions in catalogData.nodes
 * supply shared fields (e.g. recipientId) missing from the palette slice.
 */
export function resolveNodeConfigSchema(catalogData, workflowType, paletteType) {
  if (paletteType === "whatsapp.notify" || paletteType === "whatsapp.await") {
    return buildUnifiedMessageSchema(catalogData, paletteType === "whatsapp.notify" ? ".notify" : ".await");
  }

  const isCollectInput =
    paletteType === PALETTE.COLLECT_INPUT || workflowType === "COLLECT_INPUT";
  const usesWorkflowPicker =
    paletteType === PALETTE.INVOKE_WORKFLOW ||
    paletteType === PALETTE.TRANSFER_WORKFLOW ||
    workflowType === "INVOKE_WORKFLOW" ||
    workflowType === "TRANSFER_WORKFLOW";

  if (!catalogData) {
    if (isCollectInput) return { ...COLLECT_INPUT_CONFIG_SCHEMA };
    if (usesWorkflowPicker) return { ...WORKFLOW_PICKER_CONFIG_SCHEMA };
    return {};
  }

  let paletteSchema = {};

  if (paletteType) {
    const paletteEntry = getPaletteCatalogEntries(catalogData).find(
      (entry) => entry.paletteType === paletteType
    );
    if (paletteEntry?.configSchema && Object.keys(paletteEntry.configSchema).length > 0) {
      paletteSchema = { ...paletteEntry.configSchema };
    }
  }

  const genericNode = (catalogData?.nodes ?? []).find((node) => node.type === workflowType);
  const genericSchema = genericNode?.configSchema ?? {};
  const merged = { ...paletteSchema };

  for (const [key, field] of Object.entries(genericSchema)) {
    if (!(key in merged) && field?.type) {
      merged[key] = field;
    }
  }

  if (isCollectInput) {
    const fallback = { ...COLLECT_INPUT_CONFIG_SCHEMA };
    const result = { ...fallback, ...merged };
    const catalogExpectedType = merged.expectedType;
    if (catalogExpectedType?.options?.length) {
      result.expectedType = { ...fallback.expectedType, ...catalogExpectedType };
    }
    result.expectedType = {
      ...result.expectedType,
      options: filterCollectInputExpectedTypeOptions(result.expectedType?.options),
    };
    return result;
  }

  if (usesWorkflowPicker) {
    return { ...WORKFLOW_PICKER_CONFIG_SCHEMA, ...merged };
  }

  if (Object.keys(merged).length > 0) return merged;
  return genericSchema;
}
